import React, { useContext, useRef, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import { addSeconds, format } from "date-fns";
import { getTimezoneOffset } from "date-fns-tz";
import { t } from 'i18next';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';

import { LayoutContext, ILayoutContext, ToastSeverityEnum } from "@uderly/react-uderly-ui";
import { useUderlyStore } from "@uderly/react-uderly-ui/zustand";
import Module, { ExtModuleHandle } from '../components/Module';
import Device from "../../models/Device";
import { IqStore } from "../../models/IqStore";
import { StoreBreadcrumbs } from "../components/Module/StoreBreadcrumbs";
import { unregisterDevice } from "../../api/Devices";
import { ModuleHandle } from "@uderly/react-uderly-ui";

const Devices = () => {
  const layoutContext = useContext(LayoutContext) as ILayoutContext;

  const zustandStore = useUderlyStore(s => s.store);
  const [store, setStore] = useState<IqStore | null>(zustandStore as IqStore);

  const moduleData = {
    title: <StoreBreadcrumbs store={store} path="/devices" />,
    name: t('devices'),
    path: "/devices",
    apiBasePath: '/devices'
  };

  const formatDateWithTimezone = (d?: Date | null, tz?: string) => {
    if (!d) return '';

    const timezone = tz ?? 'Europe/London';
    // For time zones where daylight savings time is applicable a Date should be passed on the
    // second parameter to ensure the offset correctly accounts for DST at that time of year.
    const timezoneOffset = getTimezoneOffset(timezone, new Date(d)); // ms
    const dt = addSeconds(new Date(d), timezoneOffset / 1000);

    return format(new Date(dt), 'd/MM/Y H:mm');
  }

  const moduleRef = useRef<ExtModuleHandle>(null);
  const deviceRef = useRef<Device | null>(null);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isRowMenuOpen = Boolean(anchorEl);

  const handleRowMenuClick = (event: React.MouseEvent<HTMLElement>, row: Device) => {
    setAnchorEl(event.currentTarget);

    deviceRef.current = row;
  };

  const handleClose = () => setAnchorEl(null);

  const onUnregisterDevice = async (deleteFromStore?: boolean) => {
    if (deviceRef.current) {
      const response = await unregisterDevice({
        deviceId: deviceRef.current.device_id,
        storeSubdomain: store?.subdomain!,
        deleteFromStore: deleteFromStore
      });

      if (response && response.status === 202) {
        layoutContext.toast.show({
          open: true,
          content: t('device-removed'),
          severity: ToastSeverityEnum.success,
          autoHideDuration: 5000,
        });

        if (moduleRef.current) {
          if (!deleteFromStore) {
            if (deviceRef.current && moduleRef.current.updateRecord)
              moduleRef.current.updateRecord({ ...deviceRef.current, is_active: false } as Device);
          } else {
            moduleRef.current.fetchRecords();
          }
        }
      } else {
        layoutContext.toast.show({
          open: true,
          content: t('remove-device-error'),
          severity: ToastSeverityEnum.error,
          autoHideDuration: 5000,
        });
      }
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'device_id',
      headerName: t('id') as string,
      minWidth: 100,
      flex: 1,
      sortable: false,
      hideable: false
    },
    {
      field: 'device_type',
      headerName: t('type') as string,
      minWidth: 100,
      sortable: false,
      hideable: false
    },
    {
      field: 'device_model',
      headerName: t('model') as string,
      minWidth: 160,
      sortable: false,
      hideable: false
    },
    // ...utc_registration_time
    {
      field: 'created_at',
      headerName: t('registered-at') as string,
      minWidth: 180,
      renderCell: (params: any) => {
        return formatDateWithTimezone(params.row.created_at, store?.timezone);
      },
      sortable: false,
      hideable: false
    },
    {
      field: 'row_menu',
      headerName: '',
      width: 60,
      renderCell: (params: any) => {
        return <>
          <Tooltip title={t('options')}>
            <IconButton
              onClick={e => handleRowMenuClick(e, params.row)}
              size="small"
              sx={{ ml: 0 }}
              aria-controls={isRowMenuOpen ? 'row-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={isRowMenuOpen ? 'true' : undefined}>
              <IconButton aria-label="delete">
                <MoreVertIcon />
              </IconButton>
            </IconButton>
          </Tooltip>
        </>;
      }
    }
  ];

  if (!store) {
    return null;
  }

  return (
    <>
      <Module
        ref={moduleRef}
        columns={columns}
        getRowClassName={row => !(row as unknown as Device).is_active ? 'unactive-row' : ''}
        initialFilters={{ store_id: store.id }}
        disableInsert
        data={moduleData} />

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={isRowMenuOpen}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>

        <MenuItem onClick={() => onUnregisterDevice(false)} disabled={!deviceRef.current?.is_active}>
          <ListItemIcon>
            <BlockIcon fontSize="small" />
          </ListItemIcon>
          {t('deactivate-device')}
        </MenuItem>

        <MenuItem onClick={() => onUnregisterDevice(true)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          {t('remove-device')}
        </MenuItem>
      </Menu>
    </>
  );
};

export default Devices;